<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <InputLabel :label="label" :tooltip="tooltip" :is-error="isError" :warning="warning">
    <div class="relative">
      <select
        class="select max-w-full text-ellipsis"
        :value="value ?? modelValue"
        :disabled="disabled"
        :class="input({ size: size ?? 'sm', variant, type: 'select' })"
        @input="onInput(($event.target as HTMLSelectElement)?.value ?? '')"
      >
        <option v-if="placeholder" disabled selected>{{ placeholder }}</option>
        <option
          v-for="option in options"
          :key="option.value ?? 'key'"
          :value="option.value ?? ''"
          :disabled="option.disabled"
        >
          {{ option.label }}
        </option>
      </select>
      <div class="chevron pointer-events-none absolute right-2 top-1/2 -translate-y-1/2">
        <ChevronDownIcon class="h-4 w-4 text-primary" />
      </div>
    </div>
    <InputHint :hint="hint" :is-error="isError" />
  </InputLabel>
</template>

<script setup lang="ts" generic="T extends 'text' | 'number' = 'text'">
import { computed } from 'vue';
import InputLabel from './InputLabel.vue';
import InputHint from './InputHint.vue';
import { input } from '@carvia/theme/lib/cva/input';
import { ChevronDownIcon } from '@heroicons/vue/24/solid';

export interface SelectOption<T extends 'text' | 'number' = 'text'> {
  label: string | null;
  value: (T extends 'number' ? number : string) | null;
  disabled?: boolean;
}

const props = defineProps<{
  label?: string;
  placeholder?: string;
  hint?: string;
  modelValue?: (T extends 'number' ? number : string) | null;
  options?: SelectOption<T>[];
  value?: string;
  type?: T;
  isError?: boolean;
  tooltip?: string;
  warning?: string;
  disabled?: boolean;
  emptyValueTo?: 'null' | 'undefined';
  variant?: 'default' | 'ghost' | 'inline';
  size?: 'default' | 'sm' | 'lg';
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: (T extends 'number' ? number : string) | null | undefined): void;
}>();

const value = computed(() => props.modelValue ?? props.value);

const onInput = (newValue: string) => {
  const emptyValue = props.emptyValueTo === 'null' ? null : undefined;
  const output =
    newValue === '' && props.emptyValueTo
      ? emptyValue
      : props.type === 'number'
        ? Number(newValue)
        : newValue;
  emit('update:modelValue', output as (T extends 'number' ? number : string) | null | undefined);
};
</script>

<style scoped>
.select {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
}
</style>
